import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Checkbox
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { styled } from '@mui/material/styles';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
}));

const PermissionsManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingRoles, setPendingRoles] = useState(new Set());
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const availableRoles = ['Developer', 'Tester', 'ProductManager'];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch('https://permit-hkgwaph0hsfhcvh6.canadacentral-01.azurewebsites.net/api/permit/users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      
      if (data.error) {
        setError(data.error);
      } else {
        // Filter out admin accounts
        const filteredUsers = (data.data || []).filter(user => {
          // Filter out users with Admin role or admin email
          const isAdmin = user.roles.some(r => r.role === 'Admin');
          const hasAdminEmail = user.email === 'admin@donutnaturales.com';
          return !isAdmin && !hasAdminEmail;
        });
        console.log('Original users:', data.data);
        console.log('Filtered users:', filteredUsers);
        setUsers(filteredUsers);
      }
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleClick = (user) => {
    setSelectedUser(user);
    // Initialize pendingRoles with current user roles
    setPendingRoles(new Set(user.roles.map(r => r.role)));
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setPendingRoles(new Set());
  };

  const handleRoleToggle = (role) => {
    setPendingRoles(prev => {
      const newRoles = new Set(prev);
      if (newRoles.has(role)) {
        newRoles.delete(role);
      } else {
        newRoles.add(role);
      }
      return newRoles;
    });
  };

  const handleSaveChanges = async () => {
    if (!selectedUser) return;

    const currentRoles = new Set(selectedUser.roles.map(r => r.role));
    const rolesToAdd = [...pendingRoles].filter(role => !currentRoles.has(role));
    const rolesToRemove = [...currentRoles].filter(role => !pendingRoles.has(role));

    try {
      // Process role additions
      for (const role of rolesToAdd) {
        const response = await fetch(
          `https://permit-hkgwaph0hsfhcvh6.canadacentral-01.azurewebsites.net/api/roles/add`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ 
              userId: selectedUser.id,
              role: role 
            })
          }
        );
        if (!response.ok) throw new Error(`Failed to add role ${role}`);
      }

      // Process role removals
      for (const role of rolesToRemove) {
        const response = await fetch(
          `https://permit-hkgwaph0hsfhcvh6.canadacentral-01.azurewebsites.net/api/roles/remove`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ 
              userId: selectedUser.id,
              role: role 
            })
          }
        );
        if (!response.ok) throw new Error(`Failed to remove role ${role}`);
      }

      setSnackbar({
        open: true,
        message: 'Roles updated successfully',
        severity: 'success'
      });
      await fetchUsers(); // Refresh the user list
      handleCloseDialog();
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to update roles: ' + err.message,
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const hasChanges = selectedUser && 
    JSON.stringify([...pendingRoles].sort()) !== 
    JSON.stringify(selectedUser.roles.map(r => r.role).sort());

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        User Permissions Management
      </Typography>

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell>User</StyledTableCell>
                <StyledTableCell>Email</StyledTableCell>
                <StyledTableCell>Current Roles</StyledTableCell>
                <StyledTableCell>Actions</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight="medium">
                        {user.key}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {user.roles.map((role, index) => (
                      <Chip
                        key={index}
                        label={role.role}
                        size="small"
                        color="primary"
                        sx={{ mr: 0.5 }}
                      />
                    ))}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleRoleClick(user)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>
          Manage Roles for {selectedUser?.key}
        </DialogTitle>
        <DialogContent>
          <List>
            {availableRoles.map((role) => (
              <ListItem key={role} dense button onClick={() => handleRoleToggle(role)}>
                <Checkbox
                  edge="start"
                  checked={pendingRoles.has(role)}
                  tabIndex={-1}
                  disableRipple
                />
                <ListItemText primary={role} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveChanges}
            disabled={!hasChanges}
            variant="contained" 
            color="primary"
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PermissionsManager; 